import { useTheme } from "@emotion/react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  forgotPasswordApi,
  resetPasswordApi,
  verifyResetOtpApi,
} from "../../../API/auth";
import { successResponseHandler } from "../../../utils/reposeAlert";
import LoginButton from "../../Login/LoginButton";
import LoginInput from "../../Login/LoginInput";
import LoginLogos from "../../Login/LoginLogos";
import "../../../theme/style.css";

const OTP_LENGTH = 6;

// Client-side mirror of the backend password policy (also displayed to the
// user). Kept in sync with the rules text below.
const passwordRules = (password) =>
  password.length >= 12 &&
  /[A-Z]/.test(password) &&
  /[a-z]/.test(password) &&
  /\d/.test(password) &&
  /[^A-Za-z0-9]/.test(password);

const ForgotPassword = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // "email" -> "otp" -> "reset"
  const [step, setStep] = useState("email");

  // Step 1
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  // Step 2
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const otpRefs = useRef([]);

  // Step 3
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  // Focus the first OTP box when entering the OTP step.
  useEffect(() => {
    if (step === "otp" && otpRefs.current[0]) {
      otpRefs.current[0].focus();
    }
  }, [step]);

  // ---- Step 1: request the reset code -------------------------------------
  const emailChangeHandler = (event) => {
    setEmail(event.target.value);
  };

  const sendCodeHandler = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    await forgotPasswordApi({ email })
      .then((res) => {
        // Always 200 (enumeration-safe). Carry the token into step 2.
        setToken(res.data.token);
        setInfoMessage(res.data.message || "");
        setOtp("");
        setOtpError("");
        setStep("otp");
      })
      .catch((err) => {
        setInfoMessage(
          err.response?.data?.message ||
            "Could not send the code. Please try again."
        );
      })
      .finally(() => {
        setEmailLoading(false);
      });
  };

  // ---- Step 2: OTP entry ---------------------------------------------------
  const setOtpDigit = (index, digit) => {
    setOtp((prev) => {
      const chars = prev.padEnd(OTP_LENGTH, " ").split("");
      chars[index] = digit || " ";
      return chars.join("").replace(/\s+$/, "");
    });
  };

  const otpChangeHandler = (index, event) => {
    const raw = event.target.value.replace(/\D/g, "");
    if (!raw) {
      setOtpDigit(index, "");
      return;
    }
    const digit = raw[raw.length - 1];
    setOtpDigit(index, digit);
    setOtpError("");
    if (index < OTP_LENGTH - 1 && otpRefs.current[index + 1]) {
      otpRefs.current[index + 1].focus();
    }
  };

  const otpKeyDownHandler = (index, event) => {
    if (event.key === "Backspace") {
      if (!otp[index] && index > 0 && otpRefs.current[index - 1]) {
        event.preventDefault();
        setOtpDigit(index - 1, "");
        otpRefs.current[index - 1].focus();
      } else {
        setOtpDigit(index, "");
      }
    } else if (event.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const otpPasteHandler = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    setOtp(pasted);
    setOtpError("");
    const nextIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    otpRefs.current[nextIndex]?.focus();
  };

  const verifyHandler = async (e) => {
    e.preventDefault();
    if (otp.length !== OTP_LENGTH) {
      setOtpError("Please enter the 6-digit code.");
      return;
    }
    setVerifyLoading(true);
    setOtpError("");
    await verifyResetOtpApi({ token, otp })
      .then((res) => {
        setResetToken(res.data.resetToken);
        setNewPassword("");
        setConfirmPassword("");
        setResetError("");
        setStep("reset");
      })
      .catch((err) => {
        setOtpError(err.response?.data?.message || "Invalid verification code.");
      })
      .finally(() => {
        setVerifyLoading(false);
      });
  };

  // ---- Step 3: set the new password ---------------------------------------
  const resetHandler = async (e) => {
    e.preventDefault();
    if (!passwordRules(newPassword)) {
      setResetError(
        "Password must be at least 12 characters and include an uppercase letter, a lowercase letter, a digit, and a special character."
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError("Passwords do not match.");
      return;
    }
    setResetLoading(true);
    setResetError("");
    await resetPasswordApi({ resetToken, newPassword, confirmPassword })
      .then((res) => {
        successResponseHandler(
          "Password Reset",
          res.data.message || "Your password has been reset. Please log in."
        );
        navigate("/login");
      })
      .catch((err) => {
        setResetError(
          err.response?.data?.message ||
            "Could not reset the password. Please try again."
        );
      })
      .finally(() => {
        setResetLoading(false);
      });
  };

  const startOver = () => {
    setStep("email");
    setToken("");
    setInfoMessage("");
    setOtp("");
    setOtpError("");
    setResetToken("");
    setNewPassword("");
    setConfirmPassword("");
    setResetError("");
  };

  // Shared sx for the password inputs (mirrors LoginInput styling).
  const passwordFieldSx = {
    margin: 0,
    "& .MuiOutlinedInput-root": {
      padding: "10px 14px",
      borderRadius: "5px",
      "& input": {
        fontSize: "16px",
        color: theme.palette.secondary.main,
        padding: 0,
      },
      "& fieldset": {
        borderColor: theme.palette.third.main,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const passwordLabelSx = {
    color: theme.palette.secondary.main,
    fontWeight: "600",
    marginBottom: "15px",
    fontSize: "15px",
  };

  return (
    <div className="login-bg">
      <div className="logo-form">
        <LoginLogos />
        <div className="form-side">
          {step === "email" ? (
            <form onSubmit={sendCodeHandler}>
              <Stack sx={{ display: "flex", gap: "30px", mx: "auto", width: "100%" }}>
                <Box>
                  <Typography
                    variant="h4"
                    component="h4"
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      color: theme.palette.secondary.main,
                      fontSize: "1.4rem",
                    }}
                  >
                    FORGOT PASSWORD
                  </Typography>
                  <Typography
                    sx={{
                      textAlign: "center",
                      color: theme.palette.gray.main,
                      fontSize: "14px",
                      marginTop: "12px",
                    }}
                  >
                    Enter your registered email and we'll send you a 6-digit code.
                  </Typography>
                </Box>

                <LoginInput
                  name="email"
                  label="Email"
                  placeholder="Enter your Email"
                  value={email}
                  onChange={emailChangeHandler}
                  type="email"
                />

                {infoMessage ? (
                  <Typography
                    sx={{
                      color: theme.palette.gray.main,
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  >
                    {infoMessage}
                  </Typography>
                ) : null}

                <LoginButton loading={emailLoading} text="Send Code" />

                <Typography
                  onClick={() => navigate("/login")}
                  sx={{
                    fontSize: "14px",
                    textAlign: "center",
                    color: theme.palette.secondary.main,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  Back to login
                </Typography>
              </Stack>
            </form>
          ) : step === "otp" ? (
            <form onSubmit={verifyHandler}>
              <Stack sx={{ display: "flex", gap: "30px", mx: "auto", width: "100%" }}>
                <Box>
                  <Typography
                    variant="h4"
                    component="h4"
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      color: theme.palette.secondary.main,
                      fontSize: "1.4rem",
                    }}
                  >
                    ENTER VERIFICATION CODE
                  </Typography>
                  <Typography
                    sx={{
                      textAlign: "center",
                      color: theme.palette.gray.main,
                      fontSize: "14px",
                      marginTop: "12px",
                    }}
                  >
                    We sent a 6-digit code to your email. It is valid for 10 minutes.
                  </Typography>
                </Box>

                <Stack
                  direction="row"
                  sx={{ gap: "10px", justifyContent: "center" }}
                  onPaste={otpPasteHandler}
                >
                  {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                    <TextField
                      key={index}
                      inputRef={(el) => (otpRefs.current[index] = el)}
                      value={otp[index] || ""}
                      onChange={(e) => otpChangeHandler(index, e)}
                      onKeyDown={(e) => otpKeyDownHandler(index, e)}
                      inputProps={{
                        inputMode: "numeric",
                        maxLength: 1,
                        "aria-label": `Digit ${index + 1}`,
                        style: {
                          textAlign: "center",
                          fontSize: "22px",
                          fontWeight: 600,
                          padding: "12px 0",
                          color: theme.palette.secondary.main,
                        },
                      }}
                      sx={{
                        width: "48px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          "& fieldset": {
                            borderColor: otpError
                              ? theme.palette.error.main
                              : theme.palette.third.main,
                          },
                          "&:hover fieldset": {
                            borderColor: theme.palette.primary.main,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    />
                  ))}
                </Stack>

                {otpError ? (
                  <Typography
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: "14px",
                      textAlign: "center",
                      marginTop: "-10px",
                    }}
                  >
                    {otpError}
                  </Typography>
                ) : null}

                <LoginButton loading={verifyLoading} text="Verify" />

                <Typography
                  onClick={startOver}
                  sx={{
                    fontSize: "14px",
                    textAlign: "center",
                    color: theme.palette.secondary.main,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  Start over
                </Typography>
              </Stack>
            </form>
          ) : (
            <form onSubmit={resetHandler}>
              <Stack sx={{ display: "flex", gap: "30px", mx: "auto", width: "100%" }}>
                <Box>
                  <Typography
                    variant="h4"
                    component="h4"
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      color: theme.palette.secondary.main,
                      fontSize: "1.4rem",
                    }}
                  >
                    RESET PASSWORD
                  </Typography>
                </Box>

                <Stack direction="column" sx={{ gap: "40px" }}>
                  <Grid container alignItems="center">
                    <Grid item xs={12}>
                      <Typography sx={passwordLabelSx}>New Password</Typography>
                      <TextField
                        margin="normal"
                        type={showNew ? "text" : "password"}
                        required
                        fullWidth
                        id="newPassword"
                        name="newPassword"
                        autoComplete="new-password"
                        placeholder="Enter your new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        sx={passwordFieldSx}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle new password visibility"
                                onClick={() => setShowNew((s) => !s)}
                                edge="end"
                              >
                                {showNew ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container alignItems="center">
                    <Grid item xs={12}>
                      <Typography sx={passwordLabelSx}>Confirm Password</Typography>
                      <TextField
                        margin="normal"
                        type={showConfirm ? "text" : "password"}
                        required
                        fullWidth
                        id="confirmPassword"
                        name="confirmPassword"
                        autoComplete="new-password"
                        placeholder="Re-enter your new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        sx={passwordFieldSx}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle confirm password visibility"
                                onClick={() => setShowConfirm((s) => !s)}
                                edge="end"
                              >
                                {showConfirm ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Stack>

                <Typography
                  sx={{
                    fontSize: "13px",
                    color: theme.palette.gray.main,
                    lineHeight: 1.6,
                  }}
                >
                  Password must be at least 12 characters and include an uppercase
                  letter, a lowercase letter, a digit, and a special character.
                </Typography>

                {resetError ? (
                  <Typography
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: "14px",
                      textAlign: "center",
                      marginTop: "-10px",
                    }}
                  >
                    {resetError}
                  </Typography>
                ) : null}

                <LoginButton loading={resetLoading} text="Reset Password" />

                <Typography
                  onClick={() => navigate("/login")}
                  sx={{
                    fontSize: "14px",
                    textAlign: "center",
                    color: theme.palette.secondary.main,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  Back to login
                </Typography>
              </Stack>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
